import { InfoRounded, WarningRounded } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { selectPlan, useAppSelector } from '@/store';
import { Assessment } from '@/types';

interface QuestionBannerProps {
  assessment: Assessment;
  isPreview?: boolean;
  hasSkippedQuestion?: boolean;
  hasCompletedQuizzes?: boolean;
}

export const QuestionBanner = ({
  isPreview,
  hasSkippedQuestion,
  hasCompletedQuizzes,
  assessment,
}: QuestionBannerProps) => {
  const { data: plan } = useAppSelector(selectPlan);

  if (isPreview) {
    return (
      <Box
        sx={[
          styles.banner,
          {
            borderColor: 'blue200',
            backgroundColor: 'blue50',
            color: 'blue200',
          },
        ]}>
        <InfoRounded fontSize="small" />
        <Typography variant="body3">
          {plan?.organizationName} requires you to listen to the episode, then
          complete the chapter quizzes. Once both are finished, you may take the
          final assessment.
        </Typography>
      </Box>
    );
  }

  if (hasSkippedQuestion) {
    return (
      <Box
        sx={[
          styles.banner,
          {
            borderColor: 'orange200',
            backgroundColor: 'orange50',
            color: 'orange200',
          },
        ]}>
        <WarningRounded fontSize="small" />
        <Typography variant="body3">
          You have outstanding questions to answer, you must complete all
          questions in this assessment before you can submit.
        </Typography>
      </Box>
    );
  }

  if (!hasCompletedQuizzes) {
    return (
      <Box
        sx={[
          styles.banner,
          {
            borderColor: 'blue200',
            backgroundColor: 'blue50',
            color: 'blue200',
          },
        ]}>
        <InfoRounded fontSize="small" />
        <Typography variant="body3">
          Listen to the episode, then complete the chapter quizzes. Once both
          are finished, you may take the assessment. A score of{' '}
          {assessment.passRate}% or higher earns your CPE credit.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={[
        styles.banner,
        {
          borderColor: 'blue200',
          backgroundColor: 'blue50',
          color: 'blue200',
        },
      ]}>
      <InfoRounded fontSize="small" />
      <Typography variant="body3">
        You need at least {assessment.passRate}% to pass this assessment.
        Don&apos;t worry, you get unlimited tries!
      </Typography>
    </Box>
  );
};

const styles = {
  banner: {
    mt: { xs: 1, md: 0 },
    mb: { xs: 1, md: 3 },
    mx: { xs: 1, md: 4 },
    borderRadius: 2,
    border: '1px solid',
    p: { xs: 1.5, md: 2 },
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 1.5, md: 1 },
  },
};
